import React from 'react';

import {Avatar} from 'src/components/avatar/avatar';
import {Separator} from 'src/components/separator';
import {UserName} from 'src/components/user-name/user-name';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {ChangeRequestMetadataReviewFeedback} from 'src/modules/ai';
import {ChangeAuthorController} from 'src/modules/change-author/change-author.controller';
import {ContextualLink} from 'src/modules/event-review/contextual-link/contextual-link';
import {useCreateAiReviewUsageCount} from 'src/modules/event-review/hooks/useCreateAiReviewUsageCount';
import {useIsInReviewStatus} from 'src/modules/event-review/hooks/useIsInReviewStatus';
import {getUsersInfo} from 'src/modules/users-info/services';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {Role} from 'src/openapi/models/Role';
import {useAppSelector} from 'src/store';

import {Group, Item} from '@pb/kit-component-group';
import {formatDate} from '@pb/kit-formatters';

type Props = {
    changeRequest: ChangeRequest,
};

const LINKS_FIELD_NAME = 'contextualLinks';

export const ChangeRequestMetadata = ({changeRequest}: Props) => {
    const userRoles = useUserRoles();
    const changeRequestAuthor = useAppSelector(getUsersInfo)?.[changeRequest.authorEmail];
    const changeRequestReviewer = useAppSelector(getUsersInfo)?.[changeRequest.reviewerEmail ?? ''];
    const changeRequestApprover = useAppSelector(getUsersInfo)?.[changeRequest.approverEmail ?? ''];
    const hasReviewerRole = userRoles.includes(Role.ROLE_UDEDT_REVIEWER);
    const isInReviewStatus = useIsInReviewStatus(changeRequest);
    const createAiReviewUsageCount = useCreateAiReviewUsageCount(changeRequest.id, hasReviewerRole);

    return (
        <Group direction="v" className="pt-l">
            <Separator/>
            <Group justify="space-between">
                <Item grow={1} style={{width: '30%'}}>
                    <Group direction="v" gap="xxs" alignItems="flex-start" style={{minWidth: 0, width: '100%'}}>
                        <Group>
                            <strong>Links:</strong>
                            <ChangeRequestMetadataReviewFeedback
                                changeRequestId={changeRequest.id}
                                fieldName={LINKS_FIELD_NAME}
                            />
                        </Group>
                        {changeRequest.contextualLinks.map((link, index) => (
                            <ContextualLink key={index} contextualLink={link}/>
                        ))}
                    </Group>
                </Item>
                <Item style={{width: '40%'}}>
                    <Group direction="v">
                        <Item style={{width: '100%'}}>
                            <Group justify="start" alignItems="center" gap="m">
                                <Item style={{width: '5.1rem'}}><strong>Author:</strong></Item>
                                <Avatar user={changeRequestAuthor}/>
                                <UserName email={changeRequest.authorEmail}/>
                                <ChangeAuthorController changeRequest={changeRequest}/>
                            </Group>
                        </Item>
                        {changeRequest?.reviewerEmail ? (
                            <Item style={{width: '100%'}}>
                                <Group justify="start" alignItems="center" gap="m">
                                    <Item style={{width: '5.1rem'}}><strong>Reviewer:</strong></Item>
                                    <Avatar user={changeRequestReviewer}/>
                                    <UserName email={changeRequest.reviewerEmail}/>
                                </Group>
                            </Item>
                        ) : null}
                        {changeRequest?.approverEmail ? (
                            <Item style={{width: '100%'}}>
                                <Group justify="start" alignItems="center" gap="m">
                                    <Item style={{width: '5.1rem'}}><strong>Approver:</strong></Item>
                                    <Avatar user={changeRequestApprover}/>
                                    <UserName email={changeRequest.approverEmail}/>
                                </Group>
                            </Item>
                        ) : null}
                    </Group>
                </Item>
                <Item style={{maxWidth: '30%'}}>
                    <Group direction="v">
                        <Group justify="space-between" gap="s">
                            <Item style={{width: '5.7rem'}}> <strong>Due Date:</strong> </Item>
                            {formatDate(changeRequest.dueDate)}
                        </Group>
                        {isInReviewStatus ? (
                            <Group justify="space-between" gap="xs">
                                <Item style={{width: '11rem'}}> <strong>AI Review (by Author):</strong> </Item>
                                {createAiReviewUsageCount}
                            </Group>
                        ) : null}
                    </Group>
                </Item>
            </Group>
        </Group>
    );
};
